/**
 * 旋转中心调试可视对象。
 * 负责显示当前旋转中心使用的包围盒，以及旋转中心位置的十字标识。
 */
import * as THREE from 'three/webgpu';
import { SceneAuxiliaryObjectRegistry } from '../scene-object';
import {
    ScreenSpaceSolidLineNodeMaterial,
    setScreenSpaceSolidLineGeometryOffsets,
} from '../material/ScreenSpaceSolidLineNodeMaterial';


/** 旋转中心标记统一颜色，中心十字与拓扑代理高亮必须使用同一颜色。 */
export const ROTATION_CENTER_MARKER_COLOR = 0xff3355;

/**
 * 旋转中心调试可视对象显示状态。
 *
 * 设计说明：
 * 1. 调试显示只有有限几种输出形态，使用枚举表达状态语义；
 * 2. 包围盒与中心点分离，便于空视口回退 `controls.target` 时只显示十字中心。
 */
export enum RotationCenterDebugVisualState {
    /** 关闭全部旋转中心调试对象。 */
    Hidden = 'hidden',
    /** 仅显示旋转中心十字标识。 */
    CenterOnly = 'center-only',
    /** 同时显示旋转中心包围盒和十字标识。 */
    BoundsAndCenter = 'bounds-and-center',
}

/**
 * 旋转中心调试可视对象更新参数。
 */
export interface RotationCenterDebugVisualUpdateOptions {
    /** 调试显示状态。 */
    state: RotationCenterDebugVisualState;
    /** 当前旋转中心。 */
    center?: THREE.Vector3;
    /** 当前旋转中心使用的包围盒。 */
    bounds?: THREE.Box3;
}

/**
 * 支持现有辅助对象忽略标记的 Object3D。
 */
interface RotationCenterDebugObject extends THREE.Object3D {
    /** 是否忽略该辅助对象，不参与选择、导出与包围盒统计。 */
    ignore?: boolean;
}

/**
 * 旋转中心调试可视对象。
 *
 * 设计说明：
 * 1. 使用一个根 Group 管理包围盒线框与十字中心，便于统一显隐和释放；
 * 2. 包围盒与十字均使用 `LineSegments`，避免调试功能引入额外 mesh 体量；
 * 3. 所有几何体和材质由本类创建并在 `dispose()` 中释放，生命周期闭合。
 */
export class RotationCenterDebugVisual {
    /** 调试根对象名称。 */
    public static readonly ROOT_NAME = 'CameraManagerRotationCenterDebugRoot';

    /** 调试包围盒线对象名称。 */
    public static readonly BOUNDS_LINES_NAME = 'CameraManagerRotationCenterDebugBounds';

    /** 调试十字中心线对象名称。 */
    public static readonly CROSS_MARKER_NAME = 'CameraManagerRotationCenterDebugCross';

    /** 旋转中心调试包围盒线颜色。 */
    private static readonly BOUNDS_COLOR = 0x00bcd4;

    /** 旋转中心调试对象渲染层级，需高于普通模型和大多数交互辅助线。 */
    private static readonly RENDER_ORDER = 9000;

    /** 屏幕空间十字标识半径，单位为像素。 */
    private static readonly CROSS_MARKER_HALF_SIZE_PIXELS = 8;

    /** 包围盒线段数量。 */
    private static readonly BOUNDS_EDGE_COUNT = 12;

    /** 每条线段的顶点数量。 */
    private static readonly VERTEX_COUNT_PER_EDGE = 2;

    /** 每个顶点的坐标分量数量。 */
    private static readonly POSITION_COMPONENT_COUNT = 3;

    /** 调试根对象。 */
    public readonly root: THREE.Group;

    /** 旋转中心包围盒线对象。 */
    public readonly boundsLines: THREE.LineSegments;

    /** 旋转中心十字标识线对象。 */
    public readonly crossMarker: THREE.LineSegments;

    /** 包围盒顶点缓存，复用数组避免每次刷新分配。 */
    private readonly boundsPositions: Float32Array = new Float32Array(
        RotationCenterDebugVisual.BOUNDS_EDGE_COUNT
        * RotationCenterDebugVisual.VERTEX_COUNT_PER_EDGE
        * RotationCenterDebugVisual.POSITION_COMPONENT_COUNT,
    );

    /** 包围盒复制缓存，避免调用方传入对象后被外部继续修改导致显示状态漂移。 */
    private readonly boundsCache: THREE.Box3 = new THREE.Box3();

    /**
     * 构造函数。
     *
     * @param scene 需要挂载调试对象的主场景。
     */
    public constructor(scene: THREE.Scene) {
        this.root = this.createRoot();
        // 包围盒与十字调试根同样是辅助身份，首次显示不应触发组件索引重建。
        SceneAuxiliaryObjectRegistry.markRoot(this.root);
        this.boundsLines = this.createBoundsLines();
        this.crossMarker = this.createCrossMarker();

        // 根对象统一管理两个子对象，场景只需要维护一个调试入口。
        this.root.add(this.boundsLines, this.crossMarker);
        scene.add(this.root);
        this.update({ state: RotationCenterDebugVisualState.Hidden });
    }

    /**
     * 更新旋转中心调试显示状态。
     *
     * @param options 调试可视对象更新参数。
     */
    public update(options: RotationCenterDebugVisualUpdateOptions): void {
        if (options.state === RotationCenterDebugVisualState.Hidden) {
            this.hide();
            return;
        }

        if (!options.center) {
            throw new Error('旋转中心调试显示更新失败：缺少旋转中心。');
        }

        // 中心十字始终跟随最终旋转中心，包围盒只在存在有效视口内模型时显示。
        this.root.visible = true;
        this.crossMarker.visible = true;
        this.crossMarker.position.copy(options.center);
        this.crossMarker.scale.setScalar(1);
        this.crossMarker.updateMatrixWorld(true);

        if (options.state === RotationCenterDebugVisualState.CenterOnly) {
            this.boundsLines.visible = false;
            return;
        }

        if (!options.bounds || options.bounds.isEmpty()) {
            throw new Error('旋转中心调试显示更新失败：缺少有效包围盒。');
        }

        this.updateBoundsLines(options.bounds);
        this.boundsLines.visible = true;
    }

    /**
     * 释放调试对象持有的 three.js 资源。
     */
    public dispose(): void {
        if (this.root.parent) {
            this.root.parent.remove(this.root);
        }

        this.disposeLineSegments(this.boundsLines);
        this.disposeLineSegments(this.crossMarker);
        this.root.clear();
    }

    /**
     * 创建调试根对象。
     *
     * @returns 调试根对象。
     */
    private createRoot(): THREE.Group {
        const root = new THREE.Group();
        root.name = RotationCenterDebugVisual.ROOT_NAME;
        this.markAsIgnoredDebugObject(root);
        return root;
    }

    /**
     * 创建旋转中心包围盒线对象。
     *
     * @returns 包围盒线对象。
     */
    private createBoundsLines(): THREE.LineSegments {
        const geometry = new THREE.BufferGeometry();
        const material = new THREE.LineBasicMaterial({
            color: RotationCenterDebugVisual.BOUNDS_COLOR,
            transparent: true,
            opacity: 0.95,
            depthTest: false,
            depthWrite: false,
        });
        geometry.setAttribute('position', new THREE.BufferAttribute(this.boundsPositions, 3));

        const boundsLines = new THREE.LineSegments(geometry, material);
        boundsLines.name = RotationCenterDebugVisual.BOUNDS_LINES_NAME;
        boundsLines.frustumCulled = false;
        boundsLines.renderOrder = RotationCenterDebugVisual.RENDER_ORDER;
        boundsLines.visible = false;
        this.markAsIgnoredDebugObject(boundsLines);
        return boundsLines;
    }

    /**
     * 创建旋转中心十字标识线对象。
     *
     * @returns 十字标识线对象。
     */
    private createCrossMarker(): THREE.LineSegments {
        const geometry = new THREE.BufferGeometry();
        const material = new ScreenSpaceSolidLineNodeMaterial({
            color: ROTATION_CENTER_MARKER_COLOR,
            transparent: true,
            opacity: 1,
            depthTest: false,
            depthWrite: false,
            linewidth: 1,
        });

        this.writeCrossMarkerScreenOffsets(geometry);

        const crossMarker = new THREE.LineSegments(geometry, material);
        crossMarker.name = RotationCenterDebugVisual.CROSS_MARKER_NAME;
        crossMarker.frustumCulled = false;
        crossMarker.renderOrder = RotationCenterDebugVisual.RENDER_ORDER + 1;
        crossMarker.visible = false;
        this.markAsIgnoredDebugObject(crossMarker);
        return crossMarker;
    }

    /**
     * 写入十字标识的屏幕空间偏移。
     *
     * @param geometry 十字标识线几何体。
     */
    private writeCrossMarkerScreenOffsets(geometry: THREE.BufferGeometry): void {
        const halfSize = RotationCenterDebugVisual.CROSS_MARKER_HALF_SIZE_PIXELS;

        // 十字标识使用屏幕平面的水平线和垂直线，避免随相机距离放大遮挡局部细节。
        setScreenSpaceSolidLineGeometryOffsets(geometry, [
            [-halfSize, 0],
            [halfSize, 0],
            [0, -halfSize],
            [0, halfSize],
        ]);
    }

    /**
     * 隐藏全部旋转中心调试对象。
     */
    private hide(): void {
        this.root.visible = false;
        this.boundsLines.visible = false;
        this.crossMarker.visible = false;
    }

    /**
     * 更新包围盒线段顶点。
     *
     * @param bounds 当前旋转中心使用的包围盒。
     */
    private updateBoundsLines(bounds: THREE.Box3): void {
        this.boundsCache.copy(bounds);

        const min = this.boundsCache.min;
        const max = this.boundsCache.max;
        const positions = this.boundsPositions;
        let index = 0;

        // 12 条边按底面、顶面、竖向边顺序写入，便于调试时直接定位坐标。
        index = this.writeLinePosition(positions, index, min.x, min.y, min.z, max.x, min.y, min.z);
        index = this.writeLinePosition(positions, index, max.x, min.y, min.z, max.x, max.y, min.z);
        index = this.writeLinePosition(positions, index, max.x, max.y, min.z, min.x, max.y, min.z);
        index = this.writeLinePosition(positions, index, min.x, max.y, min.z, min.x, min.y, min.z);
        index = this.writeLinePosition(positions, index, min.x, min.y, max.z, max.x, min.y, max.z);
        index = this.writeLinePosition(positions, index, max.x, min.y, max.z, max.x, max.y, max.z);
        index = this.writeLinePosition(positions, index, max.x, max.y, max.z, min.x, max.y, max.z);
        index = this.writeLinePosition(positions, index, min.x, max.y, max.z, min.x, min.y, max.z);
        index = this.writeLinePosition(positions, index, min.x, min.y, min.z, min.x, min.y, max.z);
        index = this.writeLinePosition(positions, index, max.x, min.y, min.z, max.x, min.y, max.z);
        index = this.writeLinePosition(positions, index, max.x, max.y, min.z, max.x, max.y, max.z);
        this.writeLinePosition(positions, index, min.x, max.y, min.z, min.x, max.y, max.z);

        const positionAttribute = this.boundsLines.geometry.getAttribute('position');
        positionAttribute.needsUpdate = true;
        this.boundsLines.geometry.computeBoundingBox();
        this.boundsLines.geometry.computeBoundingSphere();
    }

    /**
     * 写入一条线段的两个端点。
     *
     * @param positions 目标顶点数组。
     * @param index 写入起始下标。
     * @param startX 起点 X 坐标。
     * @param startY 起点 Y 坐标。
     * @param startZ 起点 Z 坐标。
     * @param endX 终点 X 坐标。
     * @param endY 终点 Y 坐标。
     * @param endZ 终点 Z 坐标。
     * @returns 下一次写入起始下标。
     */
    private writeLinePosition(
        positions: Float32Array,
        index: number,
        startX: number,
        startY: number,
        startZ: number,
        endX: number,
        endY: number,
        endZ: number,
    ): number {
        positions[index] = startX;
        positions[index + 1] = startY;
        positions[index + 2] = startZ;
        positions[index + 3] = endX;
        positions[index + 4] = endY;
        positions[index + 5] = endZ;
        return index + 6;
    }

    /**
     * 标记调试对象，使其不参与模型范围统计、选择与导出。
     *
     * @param object 需要标记的调试对象。
     */
    private markAsIgnoredDebugObject(object: THREE.Object3D): void {
        (object as RotationCenterDebugObject).ignore = true;
    }

    /**
     * 释放线段对象资源。
     *
     * @param line 需要释放的线段对象。
     */
    private disposeLineSegments(line: THREE.LineSegments): void {
        line.geometry.dispose();

        if (Array.isArray(line.material)) {
            line.material.forEach((material) => material.dispose());
            return;
        }

        line.material.dispose();
    }
}

